import { supabase } from './supabase'

/**
 * Noticing that something is broken.
 *
 * The stack names Sentry and Sentry is good. It also wants an account, a key,
 * a package in the bundle and a service that still exists in two years — for
 * three users. This is the boring version: a table, and two questions
 * answered on one screen.
 *
 * The reason it exists is concrete. This week the worker stood still for two
 * days while the app cheerfully said "wordt gelezen", and nobody learned
 * anything about it except by noticing. A failure that lands nowhere does not
 * exist until someone trips over it.
 */

export interface Problem {
  message: string
  times: number
  lastSeen: string
}

export interface StuckDocument {
  documentId: string
  uploadedAt: string
  reason: string
}

/** Reported once per message per session — a loop must not flood the table. */
const reported = new Set<string>()
let installed = false

export function installErrorReporter(userId: string): void {
  if (installed || typeof window === 'undefined') return
  installed = true

  window.addEventListener('error', (event) => {
    void report(userId, event.message, event.error?.stack)
  })

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason
    void report(
      userId,
      reason instanceof Error ? reason.message : String(reason),
      reason instanceof Error ? reason.stack : undefined,
    )
  })
}

/**
 * Report a failure that was caught and handled.
 *
 * The two window listeners above only see errors nobody caught. An error you
 * catch and turn into a polite sentence never reaches them — which is exactly
 * the kind that leaves no trace and gets asked about a day later.
 */
export async function reportProblem(message: string): Promise<void> {
  try {
    const { data } = await supabase.auth.getUser()
    if (!data.user) return
    await report(data.user.id, message)
  } catch {
    // Same rule as below, one level up: telling someone about a failure must
    // never become a second failure. If the table does not exist yet — 0026
    // not run — this is exactly the path that would otherwise throw.
  }
}

async function report(userId: string, message: string, stack?: string): Promise<void> {
  const key = message.slice(0, 200)
  if (!message || reported.has(key)) return
  reported.add(key)

  try {
    await supabase.from('client_errors').insert({
      user_id: userId,
      message: key,
      stack: stack?.slice(0, 4000) ?? null,
      // Path only, never the query string — ids live there.
      path: window.location.pathname,
      user_agent: navigator.userAgent.slice(0, 300),
    })
  } catch {
    // Reporting a failure must never itself fail loudly. If the database is
    // the thing that is broken, there is nowhere to say so anyway.
  }
}

/**
 * How long since the worker last said anything.
 *
 * Separate from stuck documents on purpose: a dead worker leaves nothing stuck
 * as long as nothing arrives. On a quiet day everything looks perfect — and
 * that is exactly the day it breaks without anyone noticing. This week that
 * lasted two days.
 *
 * Null means it has never reported at all, which is a different answer from
 * "a long time ago" and deserves different words.
 */
export async function workerSilentMinutes(): Promise<number | null> {
  const { data, error } = await supabase.rpc('worker_silent_minutes')
  if (error) throw error
  return data === null || data === undefined ? null : Number(data)
}

/** The worker ticks every minute; an hour of silence is not a hiccup. */
export const WORKER_SILENT_LIMIT = 60

export function describeWorkerSilence(minutes: number | null): string | null {
  if (minutes === null) return 'De verwerker heeft zich nog nooit gemeld.'
  if (minutes < WORKER_SILENT_LIMIT) return null
  if (minutes < 60 * 24) {
    const hours = Math.floor(minutes / 60)
    return `De verwerker is al ${hours} uur stil. Nieuwe documenten blijven wachten.`
  }
  const days = Math.floor(minutes / (60 * 24))
  return `De verwerker is al ${days} dag${days === 1 ? '' : 'en'} stil. Er wordt niets verwerkt.`
}

export async function loadProblems(): Promise<Problem[]> {
  const { data, error } = await supabase.rpc('recent_problems', { p_days: 7 })
  if (error) throw error
  return (data ?? []).map((row: Record<string, unknown>) => ({
    message: row.message as string,
    times: Number(row.times),
    lastSeen: row.last_seen as string,
  }))
}

export async function loadStuckDocuments(): Promise<StuckDocument[]> {
  const { data, error } = await supabase.rpc('stuck_documents')
  if (error) throw error
  return (data ?? []).map((row: Record<string, unknown>) => ({
    documentId: row.document_id as string,
    uploadedAt: row.uploaded_at as string,
    reason: (row.reason as string) ?? 'onbekend',
  }))
}
